import { defineStore } from 'pinia';
import { useAuthStore } from './authStore';
import { ref } from 'vue';

export const useProductStore = defineStore('product', {
    state: () => ({
        products: [],
        taxes: [],
        users: [],
        spcializedItems: [],
        loadingPercentage: 0,
        isLoading: true,
        totalFileSize: 0,
        loadedFileSize: 0,
        branchId: localStorage.getItem('branchId') || null,
        userId: localStorage.getItem('userId') || null,
        branchToken: localStorage.getItem('branchToken') || null,
        pairingDone: localStorage.getItem('pairingDone') === 'true' || false,
        jwtToken: localStorage.getItem('jwtToken') || null,
    }),
    actions: {
        async initialize(link) {
            try {
                // Extract branchId and userId from the initial link
                const urlParams = new URLSearchParams(link);
                const branchId = urlParams.get('branchID');
                const userId = urlParams.get('userId');
        
                // Get stored values from localStorage
                const storedBranchId = localStorage.getItem('branchId');
                const storedUserId = localStorage.getItem('userId');
        
                // If stored values exist and differ from the URL params, reset pairing
                if (storedBranchId && storedUserId) {
                    if (storedBranchId !== branchId || storedUserId !== userId) {
                        console.log('URL parameters differ from localStorage. Resetting pairing.');
                        this.pairingDone = false;
                        localStorage.setItem('pairingDone', 'false');
                    }
                }
        
                // If pairing is already done, skip initialization
                if (this.pairingDone) {
                    console.log('Branch already paired. Skip initialization.');
                    return;
                }
        
                // Store branchId and userId in local storage
                localStorage.setItem('branchId', branchId);
                localStorage.setItem('userId', userId);
                this.branchId = branchId;
                this.userId = userId;
        
                console.log('Branch ID and User ID saved to localStorage:', { branchId, userId });
            } catch (error) {
                console.error('Error during initialization:', error);
            }
        },

        async pairBranch(password) {
            try {
                // If pairing is already done, skip pairing
                if (this.pairingDone) {
                    console.log('Branch already paired. Skipping pairing process.');
                    await this.fetchProducts();
                    return;
                }

                // Check if branchId and userId are already set
                if (!this.branchId || !this.userId) {
                    throw new Error('Branch ID or User ID is missing');
                }

                // Call the login function from authStore
                const authStore = useAuthStore();
                await authStore.login(this.userId, password);

                // pairing as done
                this.pairingDone = true;
                localStorage.setItem('pairingDone', 'true');

                // Delete localStorage items
                localStorage.removeItem('cachedProducts');

                // Fetch products and resources
                await this.fetchProducts();

                if (window.currentBranch && window.currentBranch.Name) {
                    localStorage.setItem('branchName', window.currentBranch.Name);
                    // console.log('PriceListName savedd:', window.currentBranch.Name);
                } else {
                    console.warn('currentBranch or PriceListName is missing');
                }

            } catch (error) {
                console.error('Error pairing branch:', error);
                throw error;
            }
        },

        // Function to add hardcoded items
        addHardcodedItems() {
            const hardcodedProduct = {
                Sku: "2",
                RetailSku: "MISC-2",
                Name: "Miscellaneous Item",
                Comment: "",
                MaxQty: 99,
                Price: 0.99,
                DiscountPrice: 0.99,
                ImageUrl: "https://metactical.b-cdn.net/images/products/small/2.jpg",
                Barcodes: ["222","2222","22","2"],
            };

            const existingProduct = this.products.find(product => product.Sku === hardcodedProduct.Sku);
            if (!existingProduct) {
                this.products.push(hardcodedProduct);
                console.log('Hardcoded product added:', hardcodedProduct);
            } else {
                console.log('Hardcoded product already exists:', hardcodedProduct);
            }

            const hardcodedTip = {
                Sku: "TIP-99",
                RetailSku: "TIP-99",
                Name: "Tip",
                Comment: "",
                MaxQty: 99,
                Price: 1.00,
                DiscountPrice: 1.00,
                ImageUrl: "https://metactical.b-cdn.net/images/products/small/9.jpg",
                Barcodes: ["999","9999","99","9"],
            };
            const existingTip = this.products.find(product => product.Sku === hardcodedTip.Sku);
            if (!existingTip) {
                this.products.push(hardcodedTip);
                console.log('Hardcoded product added:', hardcodedTip);
            } else {
                console.log('Hardcoded product already exists:', hardcodedTip);
            }
            // Check if the hardcoded product already exists in the products array
            
        },

        async fetchProducts() {
            if (!this.branchId || !this.userId) {
                console.error('Branch ID or User ID is missing');
                return;
            }
        
            const chunkSize = 10000;
        
            try {
                // Fetch the list of file URLs and metadata using branchId and userId
                const response = await fetch(`/api/File/GetFiles?branchId=${this.branchId}&userId=${this.userId}`, {
                    headers: {
                        'store-id': this.branchId,
                    },
                });
                const fileUrls = await response.json();
                const totalFiles = fileUrls.length;
        
                // Reset state
                this.products = [];
                this.taxes = [];
                this.users = [];
                this.spcializedItems = [];
                this.loadingPercentage = 0;
                this.isLoading = true;
                this.totalFileSize = 0;
                this.loadedFileSize = 0;
        
                // Helper function to get the file size using a HEAD request
                const getFileSize = async (url) => {
                    const headResponse = await fetch(url, { method: 'HEAD' });
                    if (headResponse.ok) {
                        return parseInt(headResponse.headers.get('Content-Length'), 10);
                    } else {
                        console.error(`Failed to fetch file size for ${url}`);
                        return 0;
                    }
                };
        
                // First, get the total size of all files
                for (const file of fileUrls) {
                    const fileUrl = `/GenJs/${file.fileUrl}?${Date.now()}`;
                    const fileSize = await getFileSize(fileUrl);
                    this.totalFileSize += fileSize;
                }
        
                // Helper function to process large arrays in chunks
                const processDataInChunks = (dataArray, targetArray) => {
                    const total = dataArray.length;
                    let processed = 0;
        
                    const processChunk = () => {
                        const chunk = dataArray.slice(processed, processed + chunkSize);
                        targetArray.push(...chunk);
                        processed += chunkSize;
        
                        if (processed < total) {
                            // Process the next chunk asynchronously
                            setTimeout(processChunk, 0);
                        }
                    };
        
                    processChunk();
                };
        
                // Function to download and inject each file, tracking progress
                const loadFile = async (file) => {
                    return new Promise((resolve, reject) => {
                        const xhr = new XMLHttpRequest();
                        const fileUrl = `/GenJs/${file.fileUrl}?${Date.now()}`;
                        xhr.open('GET', fileUrl, true);
                        xhr.responseType = 'text'; // Expecting JavaScript code as text
        
                        // Track download progress
                        let currentFileLoaded = 0;
                        xhr.onprogress = (event) => {
                            if (event.lengthComputable) {
                                // Track only the difference between the last update and the current progress
                                const increment = event.loaded - currentFileLoaded;
                                this.loadedFileSize += increment;
                                currentFileLoaded = event.loaded;
        
                                // Calculate loading percentage
                                this.loadingPercentage = Math.min(100, Math.round((this.loadedFileSize / this.totalFileSize) * 100));
                            }
                        };
        
                        xhr.onload = () => {
                            if (xhr.status === 200) {
                                const script = document.createElement('script');
                                script.textContent = xhr.responseText;
                                document.head.appendChild(script); // Inject the script into the document
        
                                if (window.products && Array.isArray(window.products)) {
                                    processDataInChunks(window.products, this.products);
                                } else {
                                    console.warn(`No valid products array found in ${file.fileUrl}`);
                                }
        
                                if (window.taxes && Array.isArray(window.taxes)) {
                                    // Clear taxes array before processing new data
                                    this.taxes = [];
                                    processDataInChunks(window.taxes, this.taxes);
                                    console.log('Taxes data:', this.taxes);
                                } else {
                                    console.warn(`No valid taxes array found in ${file.fileUrl}`);
                                }

                                //  handle users data
                                if (window.users && Array.isArray(window.users)) {
                                    this.users = [];
                                    processDataInChunks(window.users, this.users);
                                    console.log('All Users data:', this.users);
                                } else {
                                    console.warn(`No valid users array found in ${file.fileUrl}`);
                                }

                                // handle specialized items data
                                if (window.currentBranch && Array.isArray(window.currentBranch.BranchSpecializedGroups)) {
                                    this.spcializedItems = [];
                                    processDataInChunks(window.currentBranch.BranchSpecializedGroups, this.spcializedItems);
                                    console.log('All specialized items data:', this.spcializedItems);
                                } else {
                                    console.warn('No valid BranchSpecializedGroups found in currentBranch');
                                }
        
                                resolve();
                            } else {
                                console.error(`Error loading script from ${file.fileUrl}: ${xhr.statusText}`);
                                reject(new Error(xhr.statusText));
                            }
                        };
        
                        xhr.onerror = () => {
                            console.error(`Error loading script from ${file.fileUrl}`);
                            reject(new Error('Network error'));
                        };
        
                        xhr.send();
                    });
                };
        
                // Load each file and track progress
                for (const file of fileUrls) {
                    await loadFile(file);
                }
        
                this.addHardcodedItems();

                this.loadingPercentage = 100;
                this.isLoading = false;
                console.log('All products loaded.');
                console.log('Final products:', this.products);
                console.log('Final taxes:', this.taxes);
            } catch (error) {
                console.error('Error fetching files:', error);
                this.loadingPercentage = 0;
                this.isLoading = true;
            }
        },
    },
});